import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, MapPin, Phone, User, Percent, Store, AlertCircle, Loader2, CheckCircle2, Building2 } from 'lucide-react';
import { locationsApi } from '@/api/locationsApi';
import { handleApiError, getApiErrorDetails } from '@/api/client';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import type { Location } from '@/types/api';

interface AddLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  location?: Location | null; // If provided, we're in edit mode
}

interface FormData {
  name: string;
  address: string;
  phone: string;
  managerName: string;
  commissionRate: string;
}

interface FormErrors {
  name?: string;
  commissionRate?: string;
  submit?: string;
}

export function AddLocationModal({ isOpen, onClose, onSuccess, location }: AddLocationModalProps) {
  const queryClient = useQueryClient();
  const isEditMode = !!location;
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    address: '',
    phone: '',
    managerName: '',
    commissionRate: '20.00',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Populate form when editing
  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name || '',
        address: location.address || '',
        phone: location.phone || '',
        managerName: location.managerName || '',
        commissionRate: location.commissionRate?.toString() || '20.00',
      });
    } else {
      setFormData({
        name: '',
        address: '',
        phone: '',
        managerName: '',
        commissionRate: '20.00',
      });
    }
  }, [location, isOpen]);

  const createMutation = useMutation({
    mutationFn: locationsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onSuccess?.();
      handleClose();
    },
    onError: (error: any) => {
      const errorDetails = getApiErrorDetails(error);
      
      // Handle field-specific validation errors
      if (errorDetails.isValidationError && Object.keys(errorDetails.fieldErrors).length > 0) {
        const fieldErrors: Partial<FormErrors> = {};
        Object.entries(errorDetails.fieldErrors).forEach(([field, message]) => {
          if (field === 'name') fieldErrors.name = message;
          else if (field === 'commissionRate') fieldErrors.commissionRate = message;
        });
        setErrors(prev => ({ ...prev, ...fieldErrors, submit: 'Please fix the validation errors below.' }));
      } else {
        const message = handleApiError(error);
        setErrors(prev => ({ ...prev, submit: message }));
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Location>) => locationsApi.update(location!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onSuccess?.();
      handleClose();
    },
    onError: (error: any) => {
      const errorDetails = getApiErrorDetails(error);
      
      // Handle field-specific validation errors
      if (errorDetails.isValidationError && Object.keys(errorDetails.fieldErrors).length > 0) {
        const fieldErrors: Partial<FormErrors> = {};
        Object.entries(errorDetails.fieldErrors).forEach(([field, message]) => {
          if (field === 'name') fieldErrors.name = message;
          else if (field === 'commissionRate') fieldErrors.commissionRate = message;
        });
        setErrors(prev => ({ ...prev, ...fieldErrors, submit: 'Please fix the validation errors below.' }));
      } else {
        const message = handleApiError(error);
        setErrors(prev => ({ ...prev, submit: message }));
      }
    },
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Location name is required';
    }

    const commissionRate = parseFloat(formData.commissionRate);
    if (isNaN(commissionRate) || commissionRate < 0 || commissionRate > 100) {
      newErrors.commissionRate = 'Commission rate must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const data = {
        name: formData.name.trim(),
        address: formData.address.trim() || undefined,
        phone: formData.phone.trim() || undefined,
        managerName: formData.managerName.trim() || undefined,
        commissionRate: parseFloat(formData.commissionRate),
      };
      
      if (isEditMode) {
        updateMutation.mutate(data);
      } else {
        createMutation.mutate(data);
      }
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      address: '',
      phone: '',
      managerName: '',
      commissionRate: '20.00',
    });
    setErrors({});
    setTouched({});
    createMutation.reset();
    onClose();
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    // Clear error when user types
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const commissionPresets = [0, 10, 15, 20, 25, 30];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-gray-900/60 dark:bg-charcoal-950/80 backdrop-blur-sm animate-fade-in"
        onClick={!createMutation.isPending ? handleClose : undefined}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-charcoal-900 border border-gray-200 dark:border-charcoal-700 shadow-2xl animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-charcoal-700 bg-gray-50 dark:bg-charcoal-850">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center shadow-lg">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-gray-900 dark:text-warm-cream">
                {isEditMode ? 'Edit Location' : 'Add New Location'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-warm-sand">
                {isEditMode ? 'Update location details' : 'Create a retail location or consignment partner'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={createMutation.isPending}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 rounded-xl transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-warm-sand" />
          </button>
        </div>

        {/* Error Alert */}
        {(errors.submit || errors.name || errors.commissionRate || createMutation.isError || updateMutation.isError) && (
          <div className="mx-6 mt-4">
            <ErrorAlert
              title={errors.name || errors.commissionRate ? 'Please fix the errors below' : `Failed to ${isEditMode ? 'update' : 'create'} location`}
              message={errors.submit}
              fieldErrors={{
                ...(errors.name && { name: errors.name }),
                ...(errors.commissionRate && { commissionRate: errors.commissionRate }),
              }}
              severity={errors.name || errors.commissionRate ? 'warning' : 'error'}
            />
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-5">
            {/* Location Name */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Location Name <span className="text-danger-400">*</span>
              </label>
              <div className="relative">
                <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., Downtown Phoenix Store"
                  className={`
                    w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border rounded-xl text-gray-900 dark:text-warm-cream 
                    placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 
                    transition-all
                    ${errors.name && touched.name ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600 focus:border-blue-500'}
                  `}
                  autoFocus
                />
              </div>
              {errors.name && touched.name && (
                <p className="mt-1.5 text-sm text-danger-400 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Address
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-warm-taupe" />
                <textarea
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Full street address..."
                  rows={2}
                  className="w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all resize-none"
                />
              </div>
            </div>

            {/* Phone & Manager Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                  Manager Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
                  <input
                    type="text"
                    value={formData.managerName}
                    onChange={(e) => handleChange('managerName', e.target.value)}
                    placeholder="John Doe"
                    className="w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Commission Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
                Commission Rate (%)
              </label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.commissionRate}
                  onChange={(e) => handleChange('commissionRate', e.target.value)}
                  className={`
                    w-full pl-10 pr-12 py-3 bg-white dark:bg-charcoal-800 border rounded-xl text-gray-900 dark:text-warm-cream 
                    placeholder-gray-400 dark:placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 
                    transition-all
                    ${errors.commissionRate ? 'border-danger-500' : 'border-gray-300 dark:border-charcoal-600 focus:border-blue-500'}
                  `}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 dark:text-warm-sand">
                  %
                </span>
              </div>
              {errors.commissionRate && (
                <p className="mt-1.5 text-sm text-danger-400">{errors.commissionRate}</p>
              )}
              
              {/* Commission Presets */}
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="text-xs text-gray-600 dark:text-warm-sand self-center">Quick select:</span>
                {commissionPresets.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => handleChange('commissionRate', rate.toString())}
                    className={`
                      px-2.5 py-1 rounded-lg text-xs font-medium transition-all
                      ${formData.commissionRate === rate.toString()
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-charcoal-700 text-gray-600 dark:text-warm-sand hover:bg-charcoal-600'
                      }
                    `}
                  >
                    {rate}%
                  </button>
                ))}
              </div>
              
              <p className="mt-3 text-xs text-warm-taupe">
                This percentage of each sale will be paid to the location as commission.
              </p>
            </div>

            {/* Info Box */}
            <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl">
              <div className="flex items-start gap-3">
                <Store className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-warm-cream">About Locations</p>
                  <p className="text-xs text-gray-600 dark:text-warm-sand mt-1">
                    Locations represent retail stores, consignment partners, or warehouses where 
                    inventory is stored. Each location can have its own commission rate for sales.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-charcoal-700">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary min-w-[140px] bg-blue-500 hover:bg-blue-600 focus:ring-blue-500"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {isEditMode ? 'Save Changes' : 'Create Location'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
